import React, {useState, createContext, PropsWithChildren, useContext, useEffect} from 'react';
import {ComponentDefinition, FormDocument, FormTemplate} from '@formbird/types';
import {changedDocumentService} from '../services';
import {StringIndexedObject} from '../components/utils/Types';

interface FormbirdContext<T = any> {
    document: FormDocument & StringIndexedObject;
    template: FormTemplate;
    fieldValue: any;
    fieldName: string;
    formParameters: any;
    key: number;
    message: string;
    responsiveLayout: string;
    componentDefinition: ComponentDefinition & T;
}

const Context = createContext<FormbirdContext | null>(null);

export const useFormbird = <T, >(): FormbirdContext<T> & { registeredControlName: string, valueChanged: (value: any) => void } => {
    // tslint:disable-next-line:no-non-null-assertion
    const ctx = useContext(Context)!;

    console.assert(ctx !== null, 'Formbird Context is null');

    const registeredControlName = ctx.fieldName + ctx.key;

    const [fieldValue, setFieldValue] = useState(ctx.fieldValue);
    const [componentDefinition, setComponentDefinition] = useState(ctx.componentDefinition);

    useEffect(() => {
        const docSub = changedDocumentService.watch(ctx.fieldName, ctx.document.documentId, registeredControlName)
            .subscribe((newValue: any) => {
                setFieldValue(newValue);
            });

        return () => docSub.unsubscribe();
    }, []);

    useEffect(() => {
        const docSub = changedDocumentService.watchComponentDef(ctx.fieldName, ctx.template.documentId)
            .subscribe((newValue: any) => {
                setComponentDefinition(newValue);
            });

        return () => docSub.unsubscribe();
    }, []);

    const valueChanged = (value: any) => {
        setFieldValue(value);
        changedDocumentService.valueChanged(ctx, value, registeredControlName);
    };

    return ctx && {
        ...ctx,
        fieldValue,
        valueChanged,
        componentDefinition,
        registeredControlName
    };
};

export const FormbirdContextProvider = (props: PropsWithChildren<{ ctx: FormbirdContext }>) => {
    return <Context.Provider value={props.ctx}>
        {props.children}
    </Context.Provider>;
};
